import React, { useMemo } from 'react';
import { FormLayout, Input, Button } from '../../components/ui';
import { MODULES, MODULE_CATEGORIES } from '../../utils/constants';
import {
  parseHiddenModules, parseJsonStringMap, parseCategoryOrder, parseModuleOrder, sortCategories, sortModules
} from '../../utils/churchSettings';
import type { SettingsSectionProps } from './SettingsPage';

// Which modules the parish uses, what their dashboard tiles are called, and
// in what order they appear: a number on each category decides the order of
// the groups, a number on each module its order within its group. A hidden
// module disappears from the dashboard and stops answering its URL, for
// every user — permissions under Users decide who sees the rest.

const ModulesSection = ({ form, setForm, saving, onSubmit, t }: SettingsSectionProps) => {
  const hidden = useMemo(() => new Set(parseHiddenModules(form)), [form]);
  // Raw, untrimmed, so a title being typed keeps its spaces until saved.
  const titles = useMemo(() => parseJsonStringMap(form.moduleTitles), [form]);
  const categoryOrder = useMemo(() => parseCategoryOrder(form), [form]);
  const order = useMemo(() => parseModuleOrder(form), [form]);
  // Groups and cards are shown in the chosen order, so the effect of a number
  // is visible as soon as it is typed.
  const orderedCategories = useMemo(() => sortCategories(MODULE_CATEGORIES, categoryOrder), [categoryOrder]);
  const ordered = useMemo(() => sortModules(MODULES, order), [order]);

  const setHidden = (moduleId: string, isHidden: boolean) => {
    const next = new Set(hidden);
    if (isHidden) next.add(moduleId); else next.delete(moduleId);
    setForm((prev) => ({ ...prev, hiddenModules: JSON.stringify(Array.from(next)) }));
  };

  const setTitle = (moduleId: string, title: string) => {
    const next = { ...titles };
    if (title) next[moduleId] = title; else delete next[moduleId];
    setForm((prev) => ({ ...prev, moduleTitles: JSON.stringify(next) }));
  };

  // A blank or non-positive entry removes the number rather than storing it.
  const withPosition = (map: Record<string, number>, key: string, raw: string) => {
    const next = { ...map };
    const value = Number.parseInt(raw, 10);
    if (Number.isInteger(value) && value > 0) next[key] = value; else delete next[key];
    return JSON.stringify(next);
  };
  const setCategoryOrder = (category: string, raw: string) =>
    setForm((prev) => ({ ...prev, categoryOrder: withPosition(categoryOrder, category, raw) }));
  const setOrder = (moduleId: string, raw: string) =>
    setForm((prev) => ({ ...prev, moduleOrder: withPosition(order, moduleId, raw) }));

  const showAll = () => setForm((prev) => ({ ...prev, hiddenModules: '[]' }));
  const resetTitles = () => setForm((prev) => ({ ...prev, moduleTitles: '{}' }));
  const resetOrder = () => setForm((prev) => ({ ...prev, categoryOrder: '{}', moduleOrder: '{}' }));
  const orderIsDefault = Object.keys(categoryOrder).length === 0 && Object.keys(order).length === 0;

  const orderInput = (label: string, value: number | undefined, onChange: (raw: string) => void, disabled = false) => (
    <Input
      label={label}
      type="number"
      min={1}
      step={1}
      placeholder="—"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      containerStyle={{ marginBottom: 0 }}
      style={{ textAlign: 'center', paddingLeft: '6px', paddingRight: '6px' }}
    />
  );

  const visibleCount = MODULES.length - hidden.size;

  return (
    <FormLayout
      title="Modules & Dashboard"
      submitLabel="💾 Save Modules"
      submitting={saving}
      onSubmit={onSubmit}
      maxWidth="100%"
      actions={(
        <>
          <Button type="button" variant="secondary" onClick={showAll} disabled={hidden.size === 0}>
            Show all
          </Button>
          <Button type="button" variant="secondary" onClick={resetTitles} disabled={Object.keys(titles).length === 0}>
            Reset titles
          </Button>
          <Button type="button" variant="secondary" onClick={resetOrder} disabled={orderIsDefault}>
            Reset order
          </Button>
        </>
      )}
    >
      <p style={{ color: t.textMuted, fontSize: t.fontSize.md, margin: '0 0 6px' }}>
        Untick a module your parish does not use and its tile is gone for everyone.
        Give a tile a different name if that is what your parish calls it — the
        module itself keeps working exactly as before.
      </p>
      <p style={{ color: t.textMuted, fontSize: t.fontSize.md, margin: '0 0 6px' }}>
        To set the order on the dashboard and menus, number the categories
        (1 shows first, 2 next, and so on) and then number the modules inside
        each category the same way. Anything left blank follows the numbered
        ones, in the standard order.
      </p>
      <p style={{ color: t.textAccent, fontSize: t.fontSize.sm, fontWeight: 'bold', margin: '0 0 18px' }}>
        {visibleCount} of {MODULES.length} modules shown on the dashboard.
      </p>

      {orderedCategories.map((category) => {
        const inCategory = ordered.filter((m) => m.category === category);
        if (!inCategory.length) return null;
        return (
          <div key={category} style={{ marginBottom: t.spacing.lg }}>
            <FormLayout.Section
              title={(
                <span style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px' }}>
                  <span>{category}</span>
                  <span style={{ width: '120px', flexShrink: 0 }} title={`Where the ${category} group appears; 1 shows first`}>
                    {orderInput('Category order', categoryOrder[category], (raw) => setCategoryOrder(category, raw))}
                  </span>
                </span>
              )}
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '10px' }}>
              {inCategory.map((mod) => {
                const isHidden = hidden.has(mod.id);
                return (
                  <div
                    key={mod.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '10px 12px', borderRadius: t.radius.lg,
                      border: `1px solid ${isHidden ? t.border : t.primaryBorder}`,
                      backgroundColor: isHidden ? t.bgMuted : t.bgMain,
                      opacity: isHidden ? 0.7 : 1
                    }}
                  >
                    <label
                      title={isHidden ? 'Hidden from the dashboard' : 'Shown on the dashboard'}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flexShrink: 0 }}
                    >
                      <input
                        type="checkbox"
                        checked={!isHidden}
                        onChange={(e) => setHidden(mod.id, !e.target.checked)}
                        style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '22px' }} aria-hidden="true">{mod.icon}</span>
                    </label>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Input
                        label={mod.title}
                        placeholder={mod.title}
                        value={titles[mod.id] ?? ''}
                        onChange={(e) => setTitle(mod.id, e.target.value)}
                        disabled={isHidden}
                        containerStyle={{ marginBottom: 0 }}
                      />
                    </div>
                    <div style={{ width: '68px', flexShrink: 0 }} title={`Where ${mod.title} appears within ${category}; 1 shows first`}>
                      {orderInput('Order', order[mod.id], (raw) => setOrder(mod.id, raw), isHidden)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </FormLayout>
  );
};

export default ModulesSection;
